import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  IconButton,
  Fade,
  CircularProgress,
} from '@mui/material'
import { Edit } from '@mui/icons-material'
import Layout from 'components/Layout/Layout'
import MenuTab from 'components/MenuTab/Index'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { tabDetail } from 'features/tab/tab'
import {
  OrderConfirmationService,
  OrderConfirmationData,
  OrderConfirmationItem,
} from 'services/order-confirmation.service'

import { SimpleCartService, SimpleCartItem } from 'services/simple-cart.service'
import TimingValidationDialog from 'components/TimingValidationDialog/Index'
// Payment dialog imports removed - payment now processed directly through backend

interface LocationState {
  orderTrackingToken?: string
  orderPlaced?: boolean
  fromCheckout?: boolean
  establishmentId?: number
}

function OrderConfirmation() {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [orderData, setOrderData] = useState<OrderConfirmationData | null>(null)

  const tabDetailData = useSelector(tabDetail)
  const lastSignalRUpdate = useRef<number>(0)
  const [editingTip, setEditingTip] = useState(false)
  const [newTipAmount, setNewTipAmount] = useState(0)
  const [tipUpdateLoading, setTipUpdateLoading] = useState(false)
  const [userHasEditedTip, setUserHasEditedTip] = useState(false) // Track if user manually edited tip
  const [userCalculatedTotals, setUserCalculatedTotals] = useState<{
    subtotal: number
    taxAmount: number
    tipAmount: number
    total: number
  } | null>(null)
  const [userItemIds, setUserItemIds] = useState<Set<number>>(new Set())
  const [timingValidationDialog, setTimingValidationDialog] = useState<{
    open: boolean
    message: string
  }>({ open: false, message: '' })

  const orderService = useMemo(() => new OrderConfirmationService(), [])

  // Helper function to convert order confirmation items to simple cart items
  const convertToSimpleCartItems = useCallback(
    (items: OrderConfirmationItem[]): SimpleCartItem[] => {
      return items.map((item) => ({
        menuItemId: item.menuItemId,
        quantity: item.quantity || 1,
      }))
    },
    []
  )

  // Load order details when component mounts
  const loadOrderDetails = useCallback(async () => {
    try {
      setLoading(true)

      // Get tracking token from navigation state or localStorage
      const state = location.state as LocationState
      let trackingToken = state?.orderTrackingToken

      if (!trackingToken) {
        // Check if user has an active order via backend order check
        const hasActiveOrder = await OrderConfirmationService.hasActiveOrder()
        trackingToken = hasActiveOrder ? 'active-order-via-backend' : null
      }

      console.log(
        '🔍 OrderConfirmation: Received trackingToken =',
        trackingToken
      )

      // Only perform active order checks if we have a tracking token (payment flow)
      if (trackingToken) {
        console.log('🔄 Payment flow detected - checking active order status')
        // This is a payment flow, perform tracking token validation
        // Active order checks and payment intent validation would go here
      } else {
        console.log('📋 Table view mode - loading table data directly')
        // No tracking token means we're just viewing table data
        // Skip all active order checks and go straight to table data loading
      }

      // Session management is now handled automatically via rotatingGuid cookies
      // No need to manually save tracking tokens

      // Get table and establishment info for API calls
      const tableIdStr = sessionStorage.getItem('posTableId')
      const establishmentIdStr = sessionStorage.getItem('establishmentId')

      if (!tableIdStr || !establishmentIdStr) {
        toast.error('Missing table information')
        navigate('/order-menu')
        return
      }

      // Get full table order data using get-tab-order-by-id API
      const tabResponse = await OrderConfirmationService.getTabOrderById(
        Number(tableIdStr),
        Number(establishmentIdStr)
      )

      if (tabResponse.success && tabResponse.data) {
        // Convert TabResponseDTO to OrderConfirmationData format
        interface TabData {
          tabId?: number
          orderContainsFasTabOrder?: boolean
          orders?: Array<{
            orderId: number
            total: number
            orderDate: string
            posName?: string
            subTotal: number
            tax: number
            tip: number
            items?: Array<{
              itemId: number
              posItemId?: number
              name: string
              price: number
              quantity: number
              isPaid?: boolean
              isFasTabOrder?: boolean
              seatNumber?: number
              customerName?: string
            }>
          }>
        }
        const tabData = tabResponse.data as TabData

        const items: OrderConfirmationItem[] = []

        // Process all orders and their items
        if (tabData.orders && tabData.orders.length > 0) {
          tabData.orders.forEach((order) => {
            if (order.items && order.items.length > 0) {
              order.items.forEach((item) => {
                items.push({
                  preItemId: item.itemId,
                  menuItemId: item.posItemId || item.itemId,
                  itemName: item.name || 'Unknown Item',
                  itemDescription: '',
                  unitPrice: item.price / (item.quantity || 1),
                  quantity: item.quantity || 1,
                  totalPrice: item.price || 0,
                  specialInstructions: '',
                  customerSessionId: `seat-${item.seatNumber}`,
                  customerName: item.customerName || `Seat ${item.seatNumber}`,
                  seatNumber: item.seatNumber || 1,
                  isPaid: item.isPaid,
                  isFasTabOrder: item.isFasTabOrder,
                  modifiers: [],
                })
              })
            }
          })
        }

        const orderConfirmationData = {
          preOrderId: tabData.tabId || 0,
          posSystemType: tabData.orders?.[0]?.posName || 'Unknown',
          responseTimeMs: 0,
          establishmentId: Number(establishmentIdStr),
          posTableId: Number(tableIdStr),
          customerName: 'Guest',
          subtotalAmount: tabData.orders?.[0]?.subTotal || 0,
          taxAmount: tabData.orders?.[0]?.tax || 0,
          tipAmount: tabData.orders?.[0]?.tip || 0,
          tipPercentage: 0,
          finalAmount: tabData.orders?.[0]?.total || 0,
          orderStatus: 'processing',
          paymentStatus: 'authorized',
          orderDate: tabData.orders?.[0]?.orderDate || new Date().toISOString(),
          orderTrackingToken: trackingToken,
          orderContainsFasTabOrder: tabData.orderContainsFasTabOrder || false,
          items,
        }
        setOrderData(orderConfirmationData)
        setNewTipAmount(orderConfirmationData.tipAmount)
        setUserHasEditedTip(false) // Reset tip editing state
      } else {
        // Fallback: create minimal order data if no table data found
        const orderConfirmationData = {
          preOrderId: 0,
          posSystemType: 'Unknown',
          responseTimeMs: 0,
          establishmentId: Number(establishmentIdStr),
          posTableId: Number(tableIdStr),
          customerName: 'Guest',
          subtotalAmount: 0,
          taxAmount: 0,
          tipAmount: 0,
          tipPercentage: 0,
          finalAmount: 0,
          orderStatus: 'processing',
          paymentStatus: 'authorized',
          orderDate: new Date().toISOString(),
          orderTrackingToken: trackingToken,
          orderContainsFasTabOrder: false,
          items: [],
        }

        setOrderData(orderConfirmationData)
        setNewTipAmount(0)
        setUserHasEditedTip(false) // Reset tip editing state
      }

      // POS correlation now happens instantly during order placement
      // No additional status checks needed
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading order details:', error)
      toast.error('Failed to load order details')
      navigate('/')
    } finally {
      setLoading(false)
    }
  }, [location.state, navigate])

  useEffect(() => {
    loadOrderDetails()
  }, [loadOrderDetails])

  // Watch for Redux state changes from SignalR broadcasts
  useEffect(() => {
    if (tabDetailData && orderData) {
      // Check if we still have FasTab orders after the update
      const stillHasFasTabOrders =
        tabDetailData.orderContainsFasTabOrder ||
        (tabDetailData.orders &&
          tabDetailData.orders.some(
            (order) =>
              order.items &&
              order.items.some((item: any) => item.isFasTabOrder === true)
          ))

      if (!stillHasFasTabOrders) {
        navigate('/your-tab', { replace: true })
        return
      }

      // Re-fetch fresh data when SignalR update received
      const currentTime = Date.now()
      if (currentTime - lastSignalRUpdate.current > 1000) {
        lastSignalRUpdate.current = currentTime
        loadOrderDetails()
      }
    }
  }, [tabDetailData, navigate, loadOrderDetails, orderData])

  // Success toast is now handled immediately on checkout completion
  // No polling needed since correlation is instant

  // Calculate user totals using backend when order data changes
  useEffect(() => {
    const calculateUserTotals = async () => {
      if (!orderData || !orderData.items) {
        setUserCalculatedTotals(null)
        return
      }

      try {
        // 🔥 NEW: Use payment intent totals API for accurate user calculations via backend order check
        const hasActiveOrder = await OrderConfirmationService.hasActiveOrder()
        const tableIdStr = sessionStorage.getItem('posTableId')
        const establishmentIdStr = sessionStorage.getItem('establishmentId')

        if (hasActiveOrder && tableIdStr && establishmentIdStr) {
          // Use the new dedicated payment intent totals API (backend gets rotatingGuid from cookies)
          const totalsResponse =
            await OrderConfirmationService.getPaymentIntentTotals(
              Number(tableIdStr),
              Number(establishmentIdStr)
            )

          if (totalsResponse.success && totalsResponse.data) {
            const { subtotal, tax, tip, total } = totalsResponse.data

            setUserCalculatedTotals({
              subtotal,
              taxAmount: tax,
              tipAmount: tip,
              total,
            })

            // Update tip amount to the actual dollar amount if user hasn't manually edited it
            if (!userHasEditedTip) {
              setNewTipAmount(tip)
            }

            // Store user item IDs for highlighting
            setUserItemIds(new Set(totalsResponse.data.itemIds))

            console.log('🔐 USER TOTALS from payment intent API:', {
              paymentIntent: 'via-rotatingGuid-cookie',
              subtotal,
              tax,
              tip,
              total,
              itemIds: totalsResponse.data.itemIds,
              itemCount: totalsResponse.data.itemCount,
            })
            return
          }
        }

        // Fallback: If payment intent filtering is not available, assume no user items
        // (Modern flow should always use payment intent API above)
        const userItems: typeof orderData.items = []

        if (userItems.length === 0) {
          setUserCalculatedTotals({
            subtotal: 0,
            taxAmount: 0,
            tipAmount: 0,
            total: 0,
          })
          setUserItemIds(new Set()) // Clear item IDs
          return
        }

        // Set user item IDs for highlighting (fallback method)
        setUserItemIds(
          new Set(userItems.map((item) => item.preItemId).filter(Boolean))
        )

        // Convert to simple cart items
        const simpleCartItems = convertToSimpleCartItems(userItems)

        // Get tip percentage from order data
        const tipPercentage =
          orderData.subtotalAmount > 0
            ? (orderData.tipAmount / orderData.subtotalAmount) * 100
            : 0

        // Let backend calculate everything accurately
        const backendCalculation = await SimpleCartService.calculateCart(
          simpleCartItems,
          tipPercentage
        )

        setUserCalculatedTotals({
          subtotal: backendCalculation.subtotal,
          taxAmount: backendCalculation.taxAmount,
          tipAmount: backendCalculation.tipAmount,
          total: backendCalculation.total,
        })
      } catch (error) {
        console.error('Error calculating user totals:', error)
        // Fallback to zero values on calculation error
        setUserCalculatedTotals({
          subtotal: 0,
          taxAmount: 0,
          tipAmount: 0,
          total: 0,
        })
        setUserItemIds(new Set()) // Clear item IDs on error
      }
    }

    calculateUserTotals()
  }, [orderData, convertToSimpleCartItems, orderService, userHasEditedTip])

  // Periodic completion check removed - now handled by:
  // 1. Status check in loadOrderDetails() for immediate cleanup
  // 2. POS status checking (instant correlation)
  // 3. Manual order status checks when needed

  const handleTipEdit = () => {
    // Set the input to show the current displayed tip amount (either edited amount or calculated amount)
    const currentDisplayTip = userHasEditedTip
      ? newTipAmount
      : userCalculatedTotals?.tipAmount || 0
    setNewTipAmount(currentDisplayTip)
    setEditingTip(true)
  }

  const handleTipUpdate = async () => {
    try {
      setTipUpdateLoading(true)

      if (!OrderConfirmationService.validateTipAmount(newTipAmount)) {
        toast.error('Tip amount must be $0.00 or greater')
        return
      }

      // Mark that user has manually edited the tip
      setUserHasEditedTip(true)
      setEditingTip(false)
      toast.success('Tip amount updated successfully')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating tip:', error)
      toast.error('Failed to update tip amount')
    } finally {
      setTipUpdateLoading(false)
    }
  }

  const handleTipCancel = () => {
    // Reset to original tip amount (don't mark as edited)
    setNewTipAmount(orderData?.tipAmount || 0)
    setEditingTip(false)
  }

  const handleTimingValidationClose = () => {
    setTimingValidationDialog({ open: false, message: '' })
  }

  const handleFinishPaying = async () => {
    try {
      // Get table and establishment info from sessionStorage
      const tableIdStr = sessionStorage.getItem('posTableId')
      const establishmentIdStr = sessionStorage.getItem('establishmentId')
      const hasActiveOrder = await OrderConfirmationService.hasActiveOrder()

      if (!tableIdStr || !establishmentIdStr) {
        toast.error('Unable to close tab - missing table information')
        return
      }

      if (!hasActiveOrder) {
        toast.error('Unable to process payment - no active order found')
        return
      }

      const tableId = Number(tableIdStr)
      const establishmentId = Number(establishmentIdStr)

      setTipUpdateLoading(true) // Reuse loading state for payment processing

      // Calculate total amount using USER'S totals (not entire table totals)
      const userSubtotal = userCalculatedTotals?.subtotal || 0
      const userTax = userCalculatedTotals?.taxAmount || 0
      const totalAmount = userSubtotal + userTax + newTipAmount

      // Only pass tip amount if user manually edited it, otherwise let backend calculate from pre_order
      const tipAmountToSend = userHasEditedTip ? newTipAmount : undefined

      // Process payment directly using existing payment intent (via rotatingGuid cookie)
      const result = await orderService.closeTab(
        tableId,
        establishmentId,
        totalAmount,
        tipAmountToSend // Only pass tip if user edited it, otherwise backend calculates from pre_order
      )

      if (result.success && result.paymentProcessed) {
        // Session cleanup is now handled automatically by backend
        localStorage.removeItem('fastab_active_order_id')

        // Navigate to payment success page
        navigate('/payment-successful', {
          state: {
            ammountpaid: result.amountCharged || 0, // Note: PaymentSuccessful expects 'ammountpaid' (with double 'm')
            billNo: 'payment-completed', // Payment completed via rotatingGuid
            paymentIntentId: 'payment-completed', // Payment completed via rotatingGuid
            paymentId: 'payment-completed', // PaymentSuccessful expects 'paymentId'
            establishmentId,
            tabId: orderData?.preOrderId || 0, // Use preOrderId as tabId
            guestId: 0, // Default guest ID
          },
        })
      } else if (result.isTimingValidation) {
        // Show user-friendly timing validation dialog instead of error toast
        setTimingValidationDialog({
          open: true,
          message: result.message || 'Please wait before closing your tab.',
        })
      } else {
        // Show all business logic errors in user-friendly dialogs instead of toasts
        setTimingValidationDialog({
          open: true,
          message:
            result.message ||
            'We encountered an issue closing your tab. Please try again or ask your server for assistance.',
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing payment:', error)

      // Show network/unexpected errors in user-friendly dialog instead of toast
      setTimingValidationDialog({
        open: true,
        message:
          'We had trouble connecting to process your payment. Please check your internet connection and try again, or ask your server for assistance.',
      })
    } finally {
      setTipUpdateLoading(false)
    }
  }

  if (loading) {
    return (
      <Layout title="Order Confirmation | FasTab">
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
            }}
          >
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Layout>
    )
  }

  if (!orderData) {
    return (
      <Layout title="Order Confirmation | FasTab">
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: 'center', mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Order not found
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Go Home
            </Button>
          </Paper>
        </Container>
      </Layout>
    )
  }

  return (
    <>
      <Layout title="Table Order | FasTab">
        {/* wrapper start */}
        <section className="wrapper with-footer">
          <Container maxWidth="lg" sx={{ py: 3, pb: 12 }}>
            <MenuTab yourTab />

            {/* Dynamic Header Based on Context */}
            <Fade in>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  fontWeight={600}
                  color="text.primary"
                  sx={{ mt: 2 }}
                >
                  {(() => {
                    const isFasTabOrder = orderData?.orderContainsFasTabOrder
                    return isFasTabOrder
                      ? 'FasTab Orders Summary'
                      : 'Table Order Summary'
                  })()}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {orderData?.orderContainsFasTabOrder
                    ? 'Here are the FasTab orders for your table. These orders were placed through the FasTab app.'
                    : "Here's what's been ordered for your table. You can add more items above or finalize payment when ready."}
                </Typography>
              </Box>
            </Fade>

            {/* POS correlation status alerts removed - correlation now happens instantly during order placement */}

            {/* Order Items - Your Tab First, Then Others */}
            {(() => {
              // Split items into three categories:
              // 1) Your Tab (user's items), 2) Waiter-added items (non-FasTab), 3) Other Tabs (other users' FasTab items)

              const yourItems = orderData.items.filter((i) =>
                userItemIds.has(i.preItemId)
              )
              const waiterItems = orderData.items.filter(
                (i) => !i.isFasTabOrder
              )
              const otherItems = orderData.items.filter(
                (i) => i.isFasTabOrder && !userItemIds.has(i.preItemId)
              )

              // Group helper by seat
              const groupBySeat = (items: typeof orderData.items) => {
                return items.reduce((acc, item) => {
                  const key = `seat-${item.seatNumber || 0}`
                  if (!acc[key]) {
                    acc[key] = {
                      items: [] as typeof orderData.items,
                      seatNumber: item.seatNumber || 0,
                    }
                  }
                  acc[key].items.push(item)
                  return acc
                }, {} as Record<string, { items: typeof orderData.items; seatNumber: number }>)
              }

              // Define a reusable session data type
              type SessionData = {
                items: typeof orderData.items
                customerName: string
                seatNumber: number
                isUserSession: boolean
              }

              // Your tab grouped by session as before
              const groupedYour = yourItems.reduce((groups, item) => {
                const sessionKey = item.customerSessionId || 'unknown'
                if (!groups[sessionKey]) {
                  groups[sessionKey] = {
                    items: [] as typeof orderData.items,
                    customerName: item.customerName || 'Guest',
                    seatNumber: item.seatNumber || 0,
                    isUserSession: true,
                  }
                }
                groups[sessionKey].items.push(item)
                return groups
              }, {} as Record<string, SessionData>)

              const userSession = Object.entries(groupedYour)[0] || null

              // Waiter-added items grouped by seat
              const groupedWaiter = groupBySeat(waiterItems)

              // Other tabs grouped by session (based on customerName/customerSession)
              const groupedOther = otherItems.reduce((groups, item) => {
                const sessionKey =
                  item.customerSessionId || item.customerName || 'unknown'
                if (!groups[sessionKey]) {
                  groups[sessionKey] = {
                    items: [] as typeof orderData.items,
                    customerName: item.customerName || 'Guest',
                    seatNumber: item.seatNumber || 0,
                    isUserSession: false,
                  }
                }
                groups[sessionKey].items.push(item)
                return groups
              }, {} as Record<string, SessionData>)

              const otherSessions = Object.entries(groupedOther)

              const renderSessionCard = ([sessionId, sessionData]: [
                string,
                SessionData
              ]) => (
                <Paper
                  key={sessionId}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: sessionData.isUserSession
                      ? '2px solid #1976d2'
                      : '1px solid #e0e0e0',
                    backgroundColor: sessionData.isUserSession
                      ? '#f8fbff'
                      : '#ffffff',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={
                          sessionData.isUserSession ? 'primary' : 'inherit'
                        }
                      >
                        {sessionData.isUserSession
                          ? 'Your Tab'
                          : sessionData.customerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Seat #{sessionData.seatNumber}
                      </Typography>
                    </Box>
                    {sessionData.isUserSession && (
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          backgroundColor: '#1976d2',
                          color: 'white',
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        YOU
                      </Box>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {sessionData.items.map((item, index) => (
                    <Box
                      key={`${item.menuItemId}-${item.preItemId || index}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 2,
                        px: 1,
                        borderBottom:
                          index < sessionData.items.length - 1
                            ? '1px solid #f0f0f0'
                            : 'none',
                        opacity: item.isPaid ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {item.quantity}x {item.itemName}{' '}
                          {item.isPaid && (
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                px: 0.75,
                                py: 0.25,
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                verticalAlign: 'middle',
                              }}
                            >
                              PAID
                            </Box>
                          )}
                        </Typography>

                        {item.modifiers && item.modifiers.length > 0 && (
                          <Box sx={{ ml: 2, mt: 0.5 }}>
                            {item.modifiers.map((modifier, modIndex) => (
                              <Typography
                                key={`${modifier.modifierName}-${
                                  modifier.preItemAddOnModifierId || modIndex
                                }`}
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                + {modifier.modifierName} (+$
                                {(modifier.modifierPrice || 0).toFixed(2)})
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {item.specialInstructions && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              fontStyle: 'italic',
                              fontSize: '0.875rem',
                            }}
                          >
                            Note: {item.specialInstructions}
                          </Typography>
                        )}
                      </Box>

                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color="primary"
                      >
                        ${(item.totalPrice || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )

              return (
                <>
                  {/* Render user session first */}
                  {userSession && renderSessionCard(userSession)}

                  {/* Then render the "Your Tab Total" section */}
                  {userCalculatedTotals && userCalculatedTotals.subtotal > 0 && (
                    <>
                      {(() => {
                        // Calculate only user's items using backend calculation

                        // Use backend-calculated values (will be calculated in useEffect)
                        const userSubtotal = userCalculatedTotals?.subtotal || 0
                        const userTax = userCalculatedTotals?.taxAmount || 0
                        const userTip = userCalculatedTotals?.tipAmount || 0

                        // Final total with backend-calculated values (use edited tip if user changed it)
                        const displayTip = userHasEditedTip
                          ? newTipAmount
                          : userTip
                        const userTotal = userSubtotal + userTax + displayTip

                        return (
                          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              fontWeight={600}
                            >
                              Your Tab Total
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  mb: 1,
                                }}
                              >
                                <Typography>Subtotal</Typography>
                                <Typography>
                                  ${(userSubtotal || 0).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  mb: 1,
                                }}
                              >
                                <Typography>Tax</Typography>
                                <Typography>
                                  ${(userTax || 0).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography>Tip</Typography>
                                  {!editingTip && (
                                    <IconButton
                                      size="small"
                                      onClick={handleTipEdit}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                                <Box>
                                  {editingTip ? (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <input
                                        type="number"
                                        value={newTipAmount}
                                        onChange={(e) =>
                                          setNewTipAmount(
                                            parseFloat(e.target.value) || 0
                                          )
                                        }
                                        step="0.01"
                                        min="0"
                                        style={{
                                          width: '70px',
                                          padding: '4px 6px',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          textAlign: 'right',
                                          fontSize: '14px',
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={handleTipUpdate}
                                        disabled={tipUpdateLoading}
                                        sx={{ color: 'success.main' }}
                                      >
                                        ✓
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={handleTipCancel}
                                        disabled={tipUpdateLoading}
                                      >
                                        ✕
                                      </IconButton>
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography>
                                        $
                                        {(userHasEditedTip
                                          ? newTipAmount
                                          : userTip || 0
                                        ).toFixed(2)}
                                      </Typography>
                                      {userHasEditedTip && (
                                        <Typography
                                          variant="caption"
                                          color="primary"
                                          sx={{ fontSize: '0.7rem' }}
                                        >
                                          (edited)
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 2,
                              }}
                            >
                              <Typography variant="h6" fontWeight={600}>
                                Total
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight={600}
                                color="primary"
                              >
                                ${(userTotal || 0).toFixed(2)}
                              </Typography>
                            </Box>
                          </Paper>
                        )
                      })()}
                    </>
                  )}

                  {/* NEW: Waiter-added items (non-FasTab), grouped by seat */}
                  {Object.keys(groupedWaiter).length > 0 && (
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                        Items Added by Waiter
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        These items were added by your server and must be paid
                        directly with the waiter.
                      </Typography>

                      {Object.entries(groupedWaiter)
                        .sort((a, b) => a[1].seatNumber - b[1].seatNumber)
                        .map(([seatKey, seatData]) => (
                          <Box key={seatKey} sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              Seat #{seatData.seatNumber}
                            </Typography>
                            {seatData.items.map((item, index) => (
                              <Box
                                key={`${item.menuItemId}-${
                                  item.preItemId || index
                                }`}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  py: 1,
                                  px: 1,
                                  borderBottom:
                                    index < seatData.items.length - 1
                                      ? '1px solid #f0f0f0'
                                      : 'none',
                                  opacity: item.isPaid ? 0.5 : 1,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" fontWeight={600}>
                                    {item.quantity}x {item.itemName}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color="primary"
                                >
                                  ${(item.totalPrice || 0).toFixed(2)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                    </Paper>
                  )}

                  {/* Then render other sessions if any exist */}
                  {otherSessions.length > 0 && (
                    <>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        sx={{ mt: 4, mb: 3 }}
                        color="text.primary"
                      >
                        Other Tabs at Your Table
                      </Typography>
                      {otherSessions.map(renderSessionCard)}
                    </>
                  )}
                </>
              )
            })()}
          </Container>
        </section>
        {/* wrapper end */}
      </Layout>

      {/* Fixed Bottom Action Button */}
      {orderData &&
        (() => {
          // Use backend-calculated user totals
          const userSubtotal = userCalculatedTotals?.subtotal || 0
          const userTax = userCalculatedTotals?.taxAmount || 0
          const userTip = userCalculatedTotals?.tipAmount || 0

          // Final total with backend-calculated values (use edited tip if user changed it)
          const displayTip = userHasEditedTip ? newTipAmount : userTip
          const userTotal = userSubtotal + userTax + displayTip

          // Check if user has any items (has a tab)
          const hasUserTab = userSubtotal > 0

          return (
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'white',
                borderTop: '1px solid #e0e0e0',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                p: 2,
                zIndex: 1000,
              }}
            >
              <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mx: 'auto', maxWidth: 400 }}>
                  {hasUserTab ? (
                    <>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleFinishPaying}
                        disabled={tipUpdateLoading}
                        sx={{
                          width: '100%',
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          borderRadius: 2,
                          mb: 1,
                        }}
                      >
                        {tipUpdateLoading
                          ? 'Processing Payment...'
                          : `Close Tab & Pay $${(userTotal || 0).toFixed(2)}`}
                      </Button>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
                      >
                        Your tab will auto-close and charge your card after 45
                        minutes of ordering
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontSize: '0.875rem' }}
                      >
                        You don&apos;t have a tab yet
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/order-menu')}
                        sx={{
                          width: '100%',
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      >
                        Order Now
                      </Button>
                    </>
                  )}
                </Box>
              </Container>
            </Box>
          )
        })()}

      {/* Payment dialog removed - payment now processed directly through backend */}

      {/* Timing Validation Dialog */}
      <TimingValidationDialog
        open={timingValidationDialog.open}
        onClose={handleTimingValidationClose}
        message={timingValidationDialog.message}
      />
    </>
  )
}

export default OrderConfirmation
